import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, TestAgentButtonWebRTC } from '@components';
import { CreateAgentData } from '../types';
import { useAgentOptions } from '../hooks';
import { BasicInfoTab } from './tabs/BasicInfoTab';
import { CallSettingsTab } from './tabs/CallSettingsTab';
import { PostCallTab } from './tabs/PostCallTab';
import { ConfigurationTab } from './tabs/ConfigurationTab';
import { ProcessedErrors } from '@utils/errorHandler';

interface AgentFormProps {
  mode: 'create' | 'edit';
  initialData?: CreateAgentData;
  agentId?: string;
  onSave: (
    data: CreateAgentData
  ) => Promise<
    | { success: boolean; agent?: { id: string; name: string }; errors?: ProcessedErrors }
    | { success: boolean; errors: { general: string[] } }
    | undefined
  >;
}

const AgentForm: React.FC<AgentFormProps> = ({ mode, initialData, agentId, onSave }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    voices,
    knowledgeBases,
    documents,
    callRoutes,
    phoneNumbers,
    loading: optionsLoading,
    fetchDocuments,
    refetch: refetchOptions,
  } = useAgentOptions(mode === 'edit' ? initialData?.phone_number : null);
  const [activeTab, setActiveTab] = useState('basic');
  const defaultFormData: CreateAgentData = {
    name: '',
    voice: null,
    knowledge_base: null,
    documents: [],
    call_routes: [],
    phone_number: null,
    intro_message: '',
    prompt: '',
    status: 'inactive',
    variable_extraction: '',
    web_search_enabled: false,
    all_internet: false,
    whitelist_websites: '',
    live_api_calls_enabled: false,
    live_api_calls: [],
    recording_notice_enabled: true,
    intelligent_conversation_summary: 'no',
    follow_up_enabled: false,
    email_template: null,
    email_follow_up_subject: '',
    email_follow_up_content: '',
    email_follow_up_timing: 'immediately',
    email_follow_up_recipients: [],
    sms_template: null,
    sms_follow_up_content: '',
    sms_follow_up_timing: 'immediately',
    sms_follow_up_recipients: [],
    post_call_api_request_enabled: false,
    post_api_calls: [],
    max_call_time_minutes: 15,
    max_waiting_time_seconds: 15,
    vad_type: 'server_vad',
    noise_reduction_mode: 'off',
    threshold: '0.60',
    prefix_padding: 400,
    silence: 500,
    creativity: 50,
    // Advanced settings
    call_recording: 'disabled',
    working_hours: '24/7',
    priority_level: 'medium',
    tags: '',
    calendar_integration: '',
  };

  const [formData, setFormData] = useState<CreateAgentData>(initialData || defaultFormData);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState(false);
  const stopTestRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData(prevData => {
        // Only update if the data is actually different
        if (JSON.stringify(prevData) !== JSON.stringify(initialData)) {
          return initialData;
        }
        return prevData;
      });
      // Fetch documents if knowledge base is already set
      if (initialData.knowledge_base) {
        fetchDocuments(initialData.knowledge_base);
      }
    }
  }, [mode, initialData, fetchDocuments]);

  const handleInputChange = (
    field: keyof CreateAgentData,
    value: string | number | boolean | string[] | unknown[]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleBatchInputChange = (updates: Partial<CreateAgentData>) => {
    setFormData(prev => ({ ...prev, ...updates }));

    // Clear errors for updated fields
    const updatedFields = Object.keys(updates) as Array<keyof CreateAgentData>;
    setErrors(prev => {
      const newErrors = { ...prev };
      updatedFields.forEach(field => {
        if (newErrors[field]) {
          delete newErrors[field];
        }
      });
      return newErrors;
    });
  };

  // Get phone numbers from API
  const allPhoneNumbers = phoneNumbers;

  // Transform form data before sending to API
  const transformFormData = (
    data: CreateAgentData
  ): CreateAgentData & { calendars?: string[] | null } => {
    const sanitizeNumber = (value: unknown, fallback: number) =>
      typeof value === 'number' && !Number.isNaN(value) ? value : fallback;

    const isFollowUpDisabled = !data.follow_up_enabled;

    return {
      ...data,
      max_call_time_minutes: sanitizeNumber(data.max_call_time_minutes, 30),
      max_waiting_time_seconds: sanitizeNumber(data.max_waiting_time_seconds, 30),
      voice: data.voice || null,
      knowledge_base: data.knowledge_base || null,
      phone_number: data.phone_number || null,
      name: data.name || '',
      intro_message: data.intro_message || '',
      prompt: data.prompt || '',
      variable_extraction: data.variable_extraction || '',
      web_search_enabled: data.web_search_enabled || false,
      all_internet: data.all_internet || false,
      whitelist_websites: data.whitelist_websites || '',
      recording_notice_enabled: data.recording_notice_enabled ?? true,
      email_template: isFollowUpDisabled ? null : data.email_template || null,
      email_follow_up_subject: isFollowUpDisabled ? '' : data.email_follow_up_subject || '',
      email_follow_up_content: isFollowUpDisabled ? '' : data.email_follow_up_content || '',
      email_follow_up_timing: isFollowUpDisabled
        ? 'immediately'
        : data.email_follow_up_timing || 'immediately',
      email_follow_up_recipients: isFollowUpDisabled ? [] : data.email_follow_up_recipients || [],
      sms_template: isFollowUpDisabled ? null : data.sms_template || null,
      sms_follow_up_content: isFollowUpDisabled ? '' : data.sms_follow_up_content || '',
      sms_follow_up_timing: isFollowUpDisabled
        ? 'immediately'
        : data.sms_follow_up_timing || 'immediately',
      sms_follow_up_recipients: isFollowUpDisabled ? [] : data.sms_follow_up_recipients || [],
      documents: data.documents || [],
      call_routes: data.call_routes || [],
      live_api_calls: data.live_api_calls || [],
      post_api_calls: data.post_api_calls || [],
      calendars:
        data.calendar_integration && data.calendar_integration !== ''
          ? [data.calendar_integration]
          : [],
      working_hours: data.working_hours || '24/7',
      tags: data.tags || '',
    };
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrors({});

    // Transform form data before sending
    const transformedData = transformFormData(formData);

    try {
      const result = await onSave(transformedData);
      if (result && !result.success) {
        if ('errors' in result && result.errors && 'fieldErrors' in result.errors) {
          setErrors(result.errors.fieldErrors);
        }
      } else if (result && result.success) {
        // Success - form will handle next steps
        setErrors({});
      }
    } catch (error) {
      // No-op; keep field errors cleared
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/agents');
  };

  const tabs = [
    { key: 'basic', label: t('agents.basicInformation'), icon: 'info' },
    { key: 'call-settings', label: t('agents.duringTheCall'), icon: 'summarize' },
    { key: 'post-call', label: t('agents.postCall'), icon: 'settings' },
    { key: 'configuration', label: t('agents.callsAndConfiguration'), icon: 'settings' },
  ];

  const renderTabContent = () => {
    const handleKnowledgeBaseChange = (knowledgeBaseId: string) => {
      fetchDocuments(knowledgeBaseId);
    };

    const commonProps = {
      formData,
      onInputChange: handleInputChange,
      onBatchInputChange: handleBatchInputChange,
      errors,
      voices,
      knowledgeBases,
      documents,
      callRoutes,
      phoneNumbers: allPhoneNumbers,
      loading: optionsLoading,
      onKnowledgeBaseChange: handleKnowledgeBaseChange,
      onPhoneNumbersRefresh: refetchOptions,
    };

    switch (activeTab) {
      case 'basic':
        return <BasicInfoTab {...commonProps} />;
      case 'call-settings':
        return <CallSettingsTab {...commonProps} />;
      case 'post-call':
        return <PostCallTab {...commonProps} />;
      case 'configuration':
        return <ConfigurationTab {...commonProps} />;
      default:
        return null;
    }
  };

  const getPageTitle = () => {
    return mode === 'create' ? t('agents.createNewAgent') : t('agents.editAgent');
  };

  const getPageDescription = () => {
    return mode === 'create'
      ? t('agents.configureAgentDescription')
      : t('agents.updateAgentDescription');
  };

  const getButtonText = () => {
    if (loading) {
      return mode === 'create' ? t('agents.creating') : t('agents.updating');
    }
    return mode === 'create' ? t('agents.createAgent') : t('agents.updateAgent');
  };

  return (
    <div className="w-full mx-auto p-6 agent-creation">
      {/* Page Header with Action Buttons */}
      <div className="mb-8 flex items-start justify-between gap-6">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{getPageTitle()}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">{getPageDescription()}</p>
        </div>

        {/* Action Buttons in Header - Only in edit mode */}
        {mode === 'edit' && agentId && (
          <div className="flex gap-3 flex-shrink-0">
            <TestAgentButtonWebRTC
              agentId={agentId}
              agentName={formData.name}
              disabled={loading || optionsLoading}
              testing={testing}
              onTestingChange={setTesting}
              stopTestRef={stopTestRef}
              onSave={handleSubmit}
            />
            <Button variant="gradient-multi" onClick={handleSubmit} disabled={loading}>
              {loading ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          {tabs.map(tab => {
            const isDisabled = tab.key !== 'basic' && mode === 'create';
            const isActive = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                onClick={() => !isDisabled && setActiveTab(tab.key)}
                disabled={isDisabled}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                  isActive
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : isDisabled
                      ? 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="material-icons text-sm mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="w-full">
        <Card>
          <CardContent className="p-6">
            {mode === 'create' && activeTab !== 'basic' ? (
              <div className="text-center py-12">
                <div className="text-gray-500 dark:text-gray-400 mb-4">
                  <span className="material-icons text-4xl mb-2 block">info</span>
                  <h3 className="text-lg font-semibold mb-2">
                    {t('agents.completeBasicInformationFirst')}
                  </h3>
                  <p className="text-sm">{t('agents.completeBasicInformationDescription')}</p>
                </div>
                <Button variant="gradient-multi" onClick={() => setActiveTab('basic')}>
                  {t('agents.goToBasicInformation')}
                </Button>
              </div>
            ) : (
              renderTabContent()
            )}
          </CardContent>
        </Card>

        {/* Action Buttons at Bottom */}
        {mode === 'create' ? (
          <div className="flex gap-3 mt-6 w-full">
            {activeTab === 'basic' && (
              <>
                <Button
                  variant="danger"
                  onClick={handleCancel}
                  disabled={loading}
                  className="flex-1"
                >
                  {t('agents.cancel')}
                </Button>
                <Button
                  variant="gradient-multi"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1"
                >
                  {getButtonText()}
                </Button>
              </>
            )}
          </div>
        ) : (
          /* Edit mode: Test Agent + Save buttons at bottom (same as header) */
          agentId && (
            <div className="flex gap-3 mt-6 justify-end">
              <TestAgentButtonWebRTC
                agentId={agentId}
                agentName={formData.name}
                disabled={loading || optionsLoading}
                testing={testing}
                onTestingChange={setTesting}
                stopTestRef={stopTestRef}
                onSave={handleSubmit}
              />
              <Button variant="gradient-multi" onClick={handleSubmit} disabled={loading}>
                {loading ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export { AgentForm };
